import {
  FC, useEffect, useMemo, useRef, useState,
} from 'react';
// @mui
import {
  Card, CardHeader, Box, Skeleton,
} from '@mui/material';
// components
import { useTranslation } from 'react-i18next';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { CustomChartSelect } from '../../../../components/base';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TLocation } from '../../../../types';
import { getIsOrdersLoading, getLocationStats } from '../../../../store/orders/selectors';
import { AnimatedDivider } from '../../../../components';

type TLocationsSales = {
  locationsWithColors: Array<TLocation & { color: string }>;
};

const LocationsSales: FC<TLocationsSales> = ({ locationsWithColors }) => {
  const [metric, setMetric] = useState<'netSales' | 'commissions' | 'grossSales'>('netSales');
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { t } = useTranslation(['commissions', 'dashboard', 'common']);
  const chartRef = useRef<any>(null); // Store ApexCharts instance
  const locationStats = useAppSelector(getLocationStats);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const sortedData = useMemo(() => [...locationStats]?.sort((a, b) => b[metric] - a[metric]), [locationStats, metric]);

  const series = [{
    name: metric,
    data: sortedData?.map((location) => location[metric]),
  }];

  const colors = sortedData?.map((location) => {
    const match = locationsWithColors.find((loc) => loc.name === location.name);
    return match?.color || '#ccc';
  });

  const chartOptions = useChart({
    colors,
    chart: {
      type: 'bar',
      events: {
        mounted: (chartContext) => {
          chartRef.current = chartContext?.chart;
        },
      },
    },
    plotOptions: {
      bar: {
        distributed: true,
        horizontal: false,
        columnWidth: '50%',
      },
    },
    xaxis: {
      categories: sortedData.map((location) => location.name),
    },
    yaxis: {
      labels: {
        formatter: (value: number) => `${value.toFixed(2)}${getCurrency().mark}`,
      },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex }) => {
        const value = series[seriesIndex][dataPointIndex];
        const location = sortedData[dataPointIndex]?.name || '';
        const currency = getCurrency().mark;
        const metricLabel = t(metric);

        return `
          <div style="padding: 8px; font-size: 14px; color: #333;">
            <strong>${location}</strong><br/>
            ${metricLabel}: <strong>${value.toFixed(2)} ${currency}</strong>
          </div>
        `;
      },
    },
  });

  useEffect(() => {
    if (chartRef.current && typeof chartRef.current.updateOptions === 'function') {
      chartRef.current.updateOptions({
        xaxis: {
          categories: sortedData.map((location) => location.name),
        },
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex }: any) => {
            const value = series[seriesIndex][dataPointIndex];
            const location = sortedData[dataPointIndex]?.name || '';
            const currency = getCurrency().mark;
            const metricLabel = t(metric);

            return `
              <div style="padding: 8px; font-size: 14px; color: #333;">
                <strong>${location}</strong><br/>
                ${metricLabel}: <strong>${value.toFixed(2)} ${currency}</strong>
              </div>
            `;
          },
        },
      }, false, true, true);
    }
  }, [sortedData, metric, getCurrency, t]);

  return (
    <Card>
      <CardHeader
        sx={{
          p: 2,
        }}
        title={t('salesByLocation', { ns: 'dashboard' })}
        action={(
          <CustomChartSelect
            value={metric}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setMetric(event.target.value as 'netSales' | 'commissions' | 'grossSales')}
          >
            <option value="netSales">{t('netSales')}</option>
            <option value="commissions">{t('commissions')}</option>
            <option value="grossSales">{t('grossSales')}</option>
          </CustomChartSelect>
        )}
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mt: 3, mx: 3 }} dir="ltr">
        {/* eslint-disable-next-line no-nested-ternary */}
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : (
          series?.length ? (
            <Chart
              type="bar"
              series={series}
              options={chartOptions}
              height={364}
            />
          ) : <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { LocationsSales };
